// The web's realtime connection (A4F11): one socket per tab, opened while `/me` answers an account and closed when it
// stops (sign-out empties the cache). Events invalidate the same query keys as Kotlin's client; a drop or a resync
// refetches what the screens show. The state machine is `client.ts`; this wires the browser to it.
import type { QueryClient } from '@tanstack/react-query'
import { api } from '../api/api'
import { appConfigKey } from '../appConfig/appConfig'
import { keys } from '../query/keys'
import { endSession } from '../session/session'
import { RealtimeClient, type RealtimeDeps, type SocketLike } from './client'
import type { RealtimeAction } from './topicRouter'

type Me = { user: { id: string }; memberships: { orgId: string }[] }

/** The user stream's four topics and an organization's six (a topic the person may not read is refused by the server). */
export function topicsFor(me: Me | null | undefined): string[] {
  if (!me) return []
  const user = ['notifications', 'access', 'sessions', 'invitations'].map((a) => `user:${me.user.id}:${a}`)
  const orgs = me.memberships.flatMap((m) =>
    ['members', 'teams', 'invitations', 'settings', 'access', 'tasks'].map((a) => `org:${m.orgId}:${a}`),
  )
  return [...user, ...orgs, 'broadcast:config']
}

/** `ws(s)://` on this origin: the ticket's own address is for the apps, the web's CSP and nginx are same-origin. */
export function socketUrl(path: string, origin: Location = window.location): string {
  const url = new URL(path, origin.href)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  return url.toString()
}

let holds = 0
let pendingStop: string | undefined

/**
 * A screen that must outlive its own sign-out (account deletion shows its confirmation after the server has signed the
 * device out) holds the realtime sign-out; the session ends when the last hold is released. Returns the release.
 */
export function holdSessionEnd(): () => void {
  holds++
  let released = false
  return () => {
    if (released) return
    released = true
    holds--
    if (holds === 0 && pendingStop) {
      const reason = pendingStop
      pendingStop = undefined
      endSession(reason)
    }
  }
}

/** The server ended this session: sign out now, or once a screen's hold is released. */
export function sessionRevoked(reason: string): void {
  if (holds > 0) pendingStop = reason
  else endSession(reason)
}

export function applyActions(queryClient: QueryClient, actions: RealtimeAction[]): void {
  for (const action of actions) {
    if (action.kind === 'invalidate') {
      for (const queryKey of action.prefixes) void queryClient.invalidateQueries({ queryKey })
    } else if (action.kind === 'reloadAppConfig') {
      void queryClient.invalidateQueries({ queryKey: appConfigKey })
    } else {
      sessionRevoked('session_revoked')
    }
  }
}

function browserSocket(url: string): SocketLike {
  const ws = new WebSocket(url)
  const socket: SocketLike = {
    send: (data) => ws.send(data),
    close: (code) => ws.close(code === 4008 ? 4000 : code),
    onopen: null,
    onmessage: null,
    onclose: null,
  }
  ws.onopen = () => socket.onopen?.()
  ws.onmessage = (e) => typeof e.data === 'string' && socket.onmessage?.(e.data)
  ws.onclose = (e) => socket.onclose?.(e.code)
  return socket
}

let connection: { pause: () => void; resume: () => void } | undefined

/**
 * Runs [run], which ends this device's session or replaces it by another person's (a demo persona switch or end),
 * with the live connection closed: the old session's socket would report its end as the visitor's sign-out, ahead of
 * the screen that chose it. The connection opens
 * again for whoever is signed in afterwards.
 */
export async function changingSession<T>(run: () => Promise<T>): Promise<T> {
  connection?.pause()
  try {
    return await run()
  } finally {
    connection?.resume()
  }
}

/** Starts the connection for this tab; returns the undo (tests, hot reload). */
export function installRealtime(queryClient: QueryClient): () => void {
  const me = () => queryClient.getQueryData<Me | null>(keys.me())
  const deps: RealtimeDeps = {
    ticket: async () => {
      const { data } = await api.POST('/stream/ticket', {
        params: { header: { 'Idempotency-Key': crypto.randomUUID() } },
      })
      if (!data) throw new Error('no ticket')
      return { ticket: data.ticket, url: socketUrl('/api/v1/stream') }
    },
    open: browserSocket,
    topics: () => topicsFor(me()),
    onActions: (actions) => applyActions(queryClient, actions),
    onResync: (stream) => {
      const [kind, id] = stream.split(':')
      void queryClient.invalidateQueries({ queryKey: kind === 'org' && id ? keys.org(id) : keys.me() })
    },
    onStop: sessionRevoked,
    onForbidden: () => void queryClient.invalidateQueries({ queryKey: keys.me() }),
    random: Math.random,
  }
  const client = new RealtimeClient(deps)
  let paused = false
  const sync = () => {
    if (paused) return
    const signedIn = !!me()
    if (signedIn && (client.state === 'idle' || client.state === 'stopped')) client.start()
    else if (!signedIn && client.state !== 'idle') client.stop()
    else client.topicsChanged()
  }
  connection = {
    pause: () => {
      paused = true
      client.stop()
    },
    resume: () => {
      paused = false
      sync()
    },
  }
  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (event.query.queryKey.length === 1 && event.query.queryKey[0] === 'me') sync()
  })
  const wake = () => {
    if (document.visibilityState === 'visible') client.retryNow()
  }
  window.addEventListener('online', wake)
  document.addEventListener('visibilitychange', wake)
  sync()
  return () => {
    connection = undefined
    unsubscribe()
    window.removeEventListener('online', wake)
    document.removeEventListener('visibilitychange', wake)
    client.stop()
  }
}
